import AppLoader from '@/components/AppLoader';
import { ROUTE_DASHBOARD, ROUTE_SIGN_UP } from '@/constants/routes';
import useApplicationConfig from '@/hooks/useApplicationConfig';
import useAuth from '@/hooks/useAuth';
import useFirebase from '@/hooks/useFirebase';
import { useSearchParams } from '@/hooks/useSearchParams';
import { Box, Heading, Link, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RLink, Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import SocialLoginForm from './components/SocialLoginForm/SocialLogin.view';

export default function LoginPage() {
	const appConfig = useApplicationConfig();
	const { t } = useTranslation();
	const auth = useAuth();
	const firebase = useFirebase();
	const [isLoginIn, setIsLoginIn] = useState(false);
	const params = useSearchParams();

	function login(provider: 'google' | 'microsoft') {
		setIsLoginIn(true);
		return auth
			.login(provider)
			.then(() => {})
			.catch(() => {
				toast.error('An error happened. Please contact us for help');
			})
			.finally(() => {
				// Firebase updates are slow so we keep the loading status for 2 seconds
				setTimeout(() => setIsLoginIn(false), 2000);
			});
	}

	if (isLoginIn) {
		return <AppLoader />;
	}

	/**
	 * If firebase is not ready just render a loader and wait
	 */
	if (!firebase.isFirebaseReady) {
		return <AppLoader />;
	}

	/**
	 * If we have a token but not authenticated it means we are "login-in"
	 */
	if (!auth.isAuthenticated && firebase.token) {
		return <AppLoader />;
	}

	/**
	 * If we are authenticated we can redirect to dashboard
	 */
	if (auth.isAuthenticated) {
		const path = params.get('redirect') || ROUTE_DASHBOARD;
		return <Redirect to={path} />;
	}

	return (
		<Box
			bgColor="white"
			height="100dvh"
			display={{ base: 'flex', md: 'grid' }}
			gridTemplateColumns={{
				base: '0 1fr',
				md: '1fr 1fr',
			}}
		>
			<Box
				position="relative"
				flex="1"
				color="white"
				data-id="left"
				display={{ base: 'none', md: 'block' }}
				py={8}
				px={12}
				bg="primary.500"
			></Box>

			<Box flex="1" display="flex" flexDir="column" p={8} textAlign="center">
				<Box maxW="sm" mx="auto" my="auto">
					<Heading as="h1" color="primary.500" fontWeight={900} size={{ base: 'md', md: '2xl' }}>
						{appConfig.name}
					</Heading>
					<Box my={4} mb={8}>
						<Text> {t('Select a login provider')} </Text>
					</Box>

					<SocialLoginForm
						hasGoogle={appConfig.hasGoogleLogin}
						hasMicrosoft={appConfig.hasMicrosoftLogin}
						login={login}
					/>

					<Link color="blue.600" as={RLink} to={ROUTE_SIGN_UP}>
						{t('Login with email and password')}
					</Link>
				</Box>
			</Box>
		</Box>
	);
}
